import axios from "axios";
import { useAuthStore } from "../store/useAuthStore";
import { notifyPlanLimit } from "./planLimit";
import { refreshCookieMode } from "../config/authMode";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const REFRESH_URL = import.meta.env.VITE_REFRESH_URL;

export const api = axios.create({
  baseURL: API_BASE_URL,
  // Cookie mode: sign-in / refresh answers set the httpOnly `turaq_rt` cookie (CORS allows credentials).
  withCredentials: refreshCookieMode,
});

// Add a request interceptor to include the access token in the Authorization header
api.interceptors.request.use(
  (config) => {
    const accessToken = useAuthStore.getState().accessToken;

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// One refresh at a time: concurrent 401s (and the chat socket, see
// src/chat/runtime) all wait for the same request instead of each spending
// the refresh token. Resolves the new access token; never logs out by itself.
let refreshPromise: Promise<string> | null = null;

export function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const { refreshToken, setTokens } = useAuthStore.getState();
      // Cookie mode: an empty body makes the server read the `turaq_rt` cookie.
      if (!refreshToken && !refreshCookieMode) throw new Error("No refresh token");
      const { data } = await axios.post(REFRESH_URL || "", refreshToken ? { refresh_token: refreshToken } : {}, { withCredentials: refreshCookieMode });
      setTokens(data.access_token, data.refresh_token);
      api.defaults.headers.Authorization = `Bearer ${data.access_token}`;
      return data.access_token as string;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Add a response interceptor to handle 401 errors and attempt token refresh
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    const { refreshToken, logout, isAuthenticated } = useAuthStore.getState();

    // A 401 from sign-in / sign-up / Google / Apple / password reset means
    // "wrong credentials", not "session expired" — never try to refresh
    // there, or the real error is replaced by the refresh failure.
    const isAuthCall = /\/auth\/(sign-in|sign-up|google|apple|forgot-password|reset-password|refresh-token)$/.test(
      originalRequest?.url || "",
    );

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthCall) {
      // No refresh token → nothing to refresh with; the session is over.
      // Cookie mode: a signed-in session has no token in memory after a reload — refresh from the cookie.
      if (!refreshToken && !(refreshCookieMode && isAuthenticated)) {
        logout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const token = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (err) {
        logout();
        return Promise.reject(err);
      }
    }

    // A plan limit (403 PLAN_LIMIT) explains itself the same way on every screen.
    if (error.response?.status === 403) notifyPlanLimit(error.response?.data);

    return Promise.reject(error);
  },
);
